// SmartDashboard.jsx - Fully Responsive Dashboard with Mock Data
import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminPackages from "../components/AdminPackages";
import AdminBlog from "../components/AdminBlog";
import { LanguageContext } from "../context/LanguageContext";
import { ThemeContext } from "../context/ThemeContext";
import {
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaBell,
  FaTasks,
  FaCreditCard,
  FaChartLine,
  FaWallet,
  FaFileInvoice,
  FaSyncAlt,
  FaExclamationTriangle,
  FaSpinner,
  FaRegCheckCircle,
  FaShieldAlt,
  FaDownload,
  FaEye,
  FaArrowRight,
  FaCrown,
  FaPlus,
  FaTrash,
  FaEdit,
  FaEnvelope,
  FaBars,
  FaPlay,
  FaBoxOpen,
  FaPen
} from "react-icons/fa";
import { MdPendingActions, MdOutlineUpgrade, MdRefresh, MdWarning, MdPayment } from "react-icons/md";

// ------------------------ MODAL COMPONENT ------------------------
const Modal = ({ children, onClose, title }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
      <h3>{title}</h3>
      {children}
      <button className="modal-close" onClick={onClose}>×</button>
    </div>
  </div>
);

// ------------------------ MAIN COMPONENT ------------------------
const SmartDashboard = () => {
  // ==================== LANGUAGE & THEME ====================
  const { language: lang, toggleLanguage: toggleLang } = React.useContext(LanguageContext);
  const { theme, toggleTheme } = React.useContext(ThemeContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // ==================== REAL DATA STATES ====================
  const [user, setUser] = useState({ full_name: "جاري التحميل..." });
  const [userPackages, setUserPackages] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [adminLogs, setAdminLogs] = useState([]);

  // ==================== MOCK DATA TO KEEP UI STABLE ====================
  const [projects, setProjects] = useState([]);
  const [payments, setPayments] = useState({ history: [], currentStatus: "paid", lastPayment: null, nextPayment: null });
  const [notifications, setNotifications] = useState([]);
  const [activePackages, setActivePackages] = useState([
    { id: 1, name: "الباقة الأساسية", price: "29 $", maxProjects: 5 },
    { id: 2, name: "الباقة المتقدمة", price: "49 $", maxProjects: 15 },
    { id: 3, name: "الباقة الاحترافية", price: "99 $", maxProjects: 999 },
  ]);

  // Modal states
  const [subscription, setSubscription] = useState({ plan: "Basic", name: "الباقة الأساسية", price: "29 $", daysRemaining: 30, isActive: true });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [contactForm, setContactForm] = useState({ fullName: "", email: "", phone: "", subject: "", message: "" });
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDesc, setNewProjectDesc] = useState("");
  const [loading, setLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [upgradingPlan, setUpgradingPlan] = useState(false);
  const [autoCheckEnabled, setAutoCheckEnabled] = useState(true);
  const [lastChecked, setLastChecked] = useState(new Date());

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [newPasswordValue, setNewPasswordValue] = useState("");

  // ==================== TRANSLATIONS ====================
  const translations = {
    sidebar: {
      ar: { logo: "SmartPanel", menuDashboard: "لوحة التحكم", menuSubscription: "الاشتراك", menuPayments: "المدفوعات", menuNotifications: "الإشعارات", menuPortfolio: "الأعمال", autoCheckLabel: "تحديث تلقائي", lastCheck: "آخر تحديث" },
      en: { logo: "SmartPanel", menuDashboard: "Dashboard", menuSubscription: "Subscription", menuPayments: "Payments", menuNotifications: "Notifications", menuPortfolio: "Portfolio", autoCheckLabel: "Auto Check", lastCheck: "Last check" },
    },
    navbar: {
      ar: { welcome: "مرحباً بعودتك، {name}! 👋", subText: "لوحة تحكم ذكية مع مراقبة تلقائية للحالة" },
      en: { welcome: "Welcome Back, {name}! 👋", subText: "Smart dashboard with automatic status monitoring" },
    },
    cards: {
      ar: { subscription: "حالة الاشتراك", projects: "نظرة عامة على المشاريع", paymentHealth: "صحة الدفع", inProgress: "قيد التنفيذ", pending: "قيد المراجعة", completed: "مكتمل", projectNeedsAttention: "مشروع(مشاريع) تحتاج اهتمام", lastPayment: "آخر دفعة", nextPayment: "الدفعة القادمة", renews: "التجديد", daysLeft: "أيام متبقية" },
      en: { subscription: "Subscription Status", projects: "Projects Overview", paymentHealth: "Payment Health", inProgress: "In Progress", pending: "Pending", completed: "Completed", projectNeedsAttention: "project(s) need attention", lastPayment: "Last Payment", nextPayment: "Next Payment", renews: "Renews", daysLeft: "days left" },
    },
    projectTable: {
      ar: { title: "جميع المشاريع", checkStatus: "تحديث الحالة", projectName: "اسم المشروع", status: "الحالة", progress: "التقدم", dueDate: "تاريخ الاستحقاق", lastUpdate: "آخر تحديث", actions: "إجراءات", view: "عرض", overdue: "متأخر", addProject: "إضافة مشروع", edit: "تعديل", delete: "حذف" },
      en: { title: "All Projects Status", checkStatus: "Check Status", projectName: "Project Name", status: "Status", progress: "Progress", dueDate: "Due Date", lastUpdate: "Last Update", actions: "Actions", view: "View", overdue: "Overdue", addProject: "Add Project", edit: "Edit", delete: "Delete" },
    },
    paymentTable: {
      ar: { title: "سجل المدفوعات والفواتير", refresh: "تحديث", invoice: "رقم الفاتورة", date: "التاريخ", amount: "المبلغ", method: "طريقة الدفع", status: "الحالة", action: "إجراء", download: "تحميل", retry: "إعادة المحاولة", processing: "قيد المعالجة", paid: "مدفوع", pendingReview: "قيد المراجعة", failed: "فشل" },
      en: { title: "Payment History & Invoices", refresh: "Refresh", invoice: "Invoice #", date: "Date", amount: "Amount", method: "Method", status: "Status", action: "Action", download: "PDF", retry: "Retry", processing: "Processing", paid: "Paid", pendingReview: "Pending Review", failed: "Failed" },
    },
    buttons: {
      ar: { payNow: "ادفع الآن", upgradePlan: "ترقية الباقة", viewAllInvoices: "عرض جميع الفواتير", markAllRead: "تحديد الكل كمقروء", contactSupport: "اتصل بالدعم", send: "إرسال", cancelSubscription: "إلغاء الاشتراك", add: "إضافة", update: "تحديث" },
      en: { payNow: "Pay Now", upgradePlan: "Upgrade Plan", viewAllInvoices: "View All Invoices", markAllRead: "Mark all as read", contactSupport: "Contact Support", send: "Send", cancelSubscription: "Cancel Subscription", add: "Add", update: "Update" },
    },
    contactModal: {
      ar: { title: "تواصل مع الدعم", fullName: "الاسم الكامل", email: "البريد الإلكتروني", phone: "الهاتف", subject: "الموضوع", message: "الرسالة", sendSuccess: "تم إرسال رسالتك بنجاح" },
      en: { title: "Contact Support", fullName: "Full Name", email: "Email", phone: "Phone", subject: "Subject", message: "Message", sendSuccess: "Your message has been sent successfully" },
    },
    upgradeModal: {
      ar: { title: "اختر باقة جديدة", price: "السعر", maxProjects: "الحد الأقصى للمشاريع", subscribe: "اشترك" },
      en: { title: "Choose a new package", price: "Price", maxProjects: "Max Projects", subscribe: "Subscribe" },
    },
    statusMessages: {
      ar: { paymentProcessing: "🔄 جاري معالجة الدفع...", paymentSuccess: "✓ تمت معالجة الدفع بنجاح!", subscriptionCancelled: "✓ تم إلغاء الاشتراك", projectAdded: "✓ تم إضافة المشروع", projectUpdated: "✓ تم تحديث المشروع", projectDeleted: "✓ تم حذف المشروع", subscriptionExpiring: (days) => `⚠️ اشتراكك ينتهي بعد ${days} أيام!`, subscriptionExpired: "❌ انتهى اشتراكك!", projectOverdue: (name, days) => `مشروع "${name}" متأخر ${days} يومًا!` },
      en: { paymentProcessing: "🔄 Processing payment...", paymentSuccess: "✓ Payment processed successfully!", subscriptionCancelled: "✓ Subscription cancelled", projectAdded: "✓ Project added", projectUpdated: "✓ Project updated", projectDeleted: "✓ Project deleted", subscriptionExpiring: (days) => `⚠️ Your subscription expires in ${days} days!`, subscriptionExpired: "❌ Your subscription has expired!", projectOverdue: (name, days) => `Project "${name}" is ${days} day(s) overdue!` },
    },
    notifications: {
      ar: { title: "إشعارات وتنبيهات ذكية", noNotifications: "لا توجد إشعارات جديدة" },
      en: { title: "Smart Notifications & Alerts", noNotifications: "No new notifications" },
    },
  };

  // ==================== MOCK HANDLERS ====================
  const handlePayMonthly = () => {
    setPaymentProcessing(true);
    addNotification("payment", translations.statusMessages[lang].paymentProcessing, "medium");
    setTimeout(() => {
      setPaymentProcessing(false);
      addNotification("payment", translations.statusMessages[lang].paymentSuccess, "low");
      setPayments(prev => ({
        ...prev,
        lastPayment: { amount: subscription.price, date: new Date().toISOString() },
        nextPayment: { amount: subscription.price, date: new Date(Date.now() + 30*86400000).toISOString() }
      }));
    }, 1500);
  };

  const handleUpgradePlan = (packageId) => {
    setUpgradingPlan(true);
    setTimeout(() => {
      const selected = activePackages.find(p => p.id === packageId);
      setSubscription({
        ...subscription,
        plan: selected.name,
        name: selected.name,
        price: selected.price,
        daysRemaining: 30,
        isActive: true,
      });
      addNotification("subscription", "✨ تم ترقية الباقة بنجاح!", "low");
      setShowUpgradeModal(false);
      setUpgradingPlan(false);
    }, 1000);
  };

  const handleCancelSubscription = () => {
    if (window.confirm(lang === "ar" ? "هل أنت متأكد من إلغاء الاشتراك؟" : "Are you sure you want to cancel your subscription?")) {
      setSubscription({ ...subscription, isActive: false, daysRemaining: 0 });
      addNotification("subscription", translations.statusMessages[lang].subscriptionCancelled, "low");
    }
  };

  const handleAddProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      const res = await axios.post("/api/project_actions.php", { action: "add", user_id: user.id, name: newProjectName, description: newProjectDesc });
      if (res.data.status === "success") {
        const newProject = { id: res.data.project_id, name: newProjectName, description: newProjectDesc, status: "InProgress", progress: 0, dueDate: new Date().toISOString().split('T')[0], lastUpdate: new Date().toISOString() };
        setProjects([...projects, newProject]);
        addNotification("project", translations.statusMessages[lang].projectAdded, "low");
        setShowAddProjectModal(false);
        setNewProjectName("");
        setNewProjectDesc("");
      }
    } catch(err) { alert("Error adding project"); }
  };

  const handleUpdateProject = async () => {
    if (!editingProject) return;
    try {
      const res = await axios.post("/api/project_actions.php", { action: "update", user_id: user.id, project_id: editingProject.id, name: editingProject.name, description: editingProject.description, status: editingProject.status, progress: editingProject.progress });
      if (res.data.status === "success") {
        setProjects(projects.map(p => p.id === editingProject.id ? editingProject : p));
        addNotification("project", translations.statusMessages[lang].projectUpdated, "low");
        setEditingProject(null);
      }
    } catch(err) { alert("Error updating"); }
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm(lang === "ar" ? "حذف المشروع؟" : "Delete project?")) {
      try {
        const res = await axios.post("/api/project_actions.php", { action: "delete", user_id: user.id, project_id: projectId });
        if (res.data.status === "success") {
          setProjects(projects.filter(p => p.id !== projectId));
          addNotification("project", translations.statusMessages[lang].projectDeleted, "low");
        }
      } catch(err) { alert("Error deleting"); }
    }
  };

  const handleContactSend = () => {
    addNotification("system", translations.contactModal[lang].sendSuccess, "low");
    setShowContactModal(false);
    setContactForm({ fullName: "", email: "", phone: "", subject: "", message: "" });
  };

  const handleRefreshPaymentHistory = () => {
    addNotification("system", lang === "ar" ? "تم تحديث المدفوعات" : "Payments refreshed", "low");
  };

  const runAllChecks = () => {
    setLoading(true);
    setTimeout(() => {
      // Remove fake project overdue warnings.
      // Only warn for actual subscription if ending soon.
      if (subscription && subscription.daysRemaining <= 7 && subscription.daysRemaining > 0) {
        addNotification("subscription", translations.statusMessages[lang].subscriptionExpiring(subscription.daysRemaining), "high");
      } else if (subscription && subscription.daysRemaining <= 0) {
        addNotification("subscription", translations.statusMessages[lang].subscriptionExpired, "critical");
      }
      setLastChecked(new Date());
      setLoading(false);
    }, 800);
  };

  const addNotification = (type, message, priority) => {
    const newNotif = {
      id: Date.now(),
      type,
      message,
      time: new Date().toISOString(),
      read: false,
      priority,
    };
    setNotifications(prev => [newNotif, ...prev]);
  };

  useEffect(() => {
    if (autoCheckEnabled) {
      const interval = setInterval(runAllChecks, 30000);
      return () => clearInterval(interval);
    }
  }, [autoCheckEnabled]);

  // ==================== FETCH REAL DATA ====================
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const localUserStr = localStorage.getItem("userData") || sessionStorage.getItem("userData");
        const isLoggedIn = (localStorage.getItem("isLoggedIn") === "true" || sessionStorage.getItem("isLoggedIn") === "true");
        if (!isLoggedIn || !localUserStr) {
          window.location.href = '/signin';
          return;
        }

        const localUser = JSON.parse(localUserStr);
        setUser(localUser); // Set immediately for instant UI render
        
        const apiUrl = window.location.hostname === 'localhost' ? 'http://localhost/api/get_dashboard.php' : '/api/get_dashboard.php';
        
        const res = await axios.get(`${apiUrl}?user_id=${localUser.id}`);
        if (res.data.status === 'success') {
          const fetchedUser = res.data.user;
          setUser(fetchedUser);
          if (fetchedUser.terms_accepted == 0) {
            setShowTermsModal(true);
          }
          if (fetchedUser.role === 'admin') {
            setIsAdmin(true);
            setAllUsers(res.data.all_users);
            setAllPackages(res.data.all_packages);
            if (res.data.all_projects) setProjects(res.data.all_projects);
            fetchAdminLogs(fetchedUser.id);
          } else {
            setIsAdmin(false);
            setUserPackages(res.data.packages);
            if (res.data.projects) setProjects(res.data.projects);
            // Link first package to subscription UI if exists
            if (res.data.packages.length > 0) {
              // We'll map them in the render, but keep the mock state format
            }
          }
        } else {
           window.location.href = '/signin';
        }
      } catch (err) {
        console.error("Fetch dashboard error:", err);
      }
    };
    fetchDashboard();
  }, []);

  const handleDeleteUser = async (targetId) => {
    if(!window.confirm("حذف العميل وكافة بياناته ومشاريعه نهائياً؟")) return;
    try {
      const apiUrl = window.location.hostname === 'localhost' ? 'http://localhost/api/delete_user.php' : '/api/delete_user.php';
      const res = await axios.post(apiUrl, { admin_id: user.id, target_user_id: targetId });
      if(res.data.status === 'success') {
        setAllUsers(allUsers.filter(u => u.id !== targetId));
        setAllPackages(allPackages.filter(p => Number(p.user_id) !== Number(targetId)));
        addNotification("system", "تم حذف المستخدم بنجاح", "low");
      }
    } catch(err) { alert("حدث خطأ أثناء الحذف"); }
  };

  const handlePromoteUser = async (targetId) => {
    if(!window.confirm("ترقية العميل إلى (مشرف) بشكل دائم؟")) return;
    try {
      const apiUrl = window.location.hostname === 'localhost' ? 'http://localhost/api/promote_user.php' : '/api/promote_user.php';
      const res = await axios.post(apiUrl, { admin_id: user.id, user_id: targetId });
      if(res.data.status === 'success') {
        alert("تم الترقية لدرجة مشرف بنجاح");
        setAllUsers(allUsers.map(u => u.id === targetId ? {...u, role: 'admin'} : u));
      } else alert(res.data.message);
    } catch(err) { alert("حدث خطأ"); }
  };

  const handleOpenPasswordModal = (targetUser) => {
    setSelectedUserForPassword(targetUser);
    setNewPasswordValue("");
    setShowPasswordModal(true);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPasswordValue.length < 6) return alert("الرقم السري قصير جدًا");
    try {
      const apiUrl = window.location.hostname === 'localhost' ? 'http://localhost/api/change_user_password.php' : '/api/change_user_password.php';
      const res = await axios.post(apiUrl, { admin_id: user.id, user_id: selectedUserForPassword.id, new_password: newPasswordValue });
      if(res.data.status === 'success') {
        alert("تم تغيير كلمة المرور بنجاح");
        setShowPasswordModal(false);
        setAllUsers(allUsers.map(u => u.id === selectedUserForPassword.id ? {...u, reset_request: 0} : u));
      } else alert(res.data.message);
    } catch(err) { alert("خطأ بالاتصال"); }
  };


  const handleDeactivatePackage = async (packageId) => {
    if(!window.confirm("إلغاء تنشيط الباقة؟")) return;
    try {
      const apiUrl = window.location.hostname === 'localhost' ? 'http://localhost/api/update_package.php' : '/api/update_package.php';
      const res = await axios.post(apiUrl, { package_id: packageId, status: "inactive", admin_id: user.id });
      if(res.data.status === 'success') {
        setAllPackages(allPackages.map(p => p.id === packageId ? {...p, status: "inactive"} : p));
        addNotification("system", "تم إيقاف الباقة", "low");
      }
    } catch(err) { alert("خطأ"); }
  };

  const fetchAdminLogs = async (adminId) => {
      try {
        const apiUrl = window.location.hostname === 'localhost' ? `http://localhost/api/get_admin_logs.php?admin_id=${adminId}` : `/api/get_admin_logs.php?admin_id=${adminId}`;
        const logsRes = await axios.get(apiUrl);
        if (logsRes.data.status === 'success') setAdminLogs(logsRes.data.logs);
      } catch(e) {}
  };

  const handleActivatePackage = async (packageId) => {
    try {
      if(!window.confirm("تأكيد تفعيل الباقة للعميل؟")) return;
      const res = await axios.post("/api/update_package.php", { package_id: packageId, status: "active", admin_id: user.id });
      if(res.data.status === "success") {
        setAllPackages(allPackages.map(p => p.id === packageId ? {...p, status: "active"} : p));
        addNotification("system", "تم تفعيل الباقة بنجاح", "low");
      }
    } catch(err) { alert("حدث خطأ أثناء التفعيل"); }
  };

  const handleAcceptTerms = async () => {
    try {
      const localUser = JSON.parse(localStorage.getItem("userData"));
      const apiUrl = window.location.hostname === 'localhost' ? 'http://localhost/api/accept_terms.php' : '/api/accept_terms.php';
      await axios.post(apiUrl, { user_id: localUser.id });
      setShowTermsModal(false);
      setUser(prev => ({ ...prev, terms_accepted: 1 }));
      addNotification("system", "تم قبول الشروط بنجاح", "low");
    } catch (err) {
      alert("حدث خطأ أثناء حفظ الموافقة");
    }
  };

  // ==================== HELPER FUNCTIONS ====================
  const getProjectStatusInfo = (status) => {
    const labels = {
      ar: {
        InProgress: { label: "قيد التنفيذ", color: "#0E74AB", icon: <FaSpinner />, bg: "#E3F2FD" },
        PendingReview: { label: "قيد المراجعة", color: "#FF9800", icon: <MdPendingActions />, bg: "#FFF3E0" },
        Completed: { label: "مكتمل", color: "#4CAF50", icon: <FaRegCheckCircle />, bg: "#E8F5E9" },
      },
      en: {
        InProgress: { label: "In Progress", color: "#0E74AB", icon: <FaSpinner />, bg: "#E3F2FD" },
        PendingReview: { label: "Pending Review", color: "#FF9800", icon: <MdPendingActions />, bg: "#FFF3E0" },
        Completed: { label: "Completed", color: "#4CAF50", icon: <FaRegCheckCircle />, bg: "#E8F5E9" },
      },
    };
    return labels[lang][status] || { label: status, color: "#9E9E9E", icon: <FaClock />, bg: "#F5F5F5" };
  };

  const formatDate = (dateStr) => (dateStr ? new Date(dateStr).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US") : "");
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return lang === "ar" ? "الآن" : "Just now";
    if (diffMins < 60) return lang === "ar" ? `منذ ${diffMins} دقيقة` : `${diffMins} minutes ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return lang === "ar" ? `منذ ${diffHours} ساعة` : `${diffHours} hours ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return lang === "ar" ? `منذ ${diffDays} يوم` : `${diffDays} days ago`;
    return formatDate(dateStr);
  };

  const unreadCount = notifications.filter((n) => !n.read).length;
  const projectsNeedingAttention = projects.filter((p) => p.status !== "Completed" && new Date(p.dueDate) < new Date()).length;

  // ==================== RENDER ====================
  return (
    <div className={`dashboard-container ${lang === "ar" ? "rtl" : "ltr"}`}>
      {/* Mobile Menu Toggle */}
      <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
        <FaBars />
      </button>

      {/* Removed Language Switcher as Navbar handles it globally */}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileMenuOpen ? "mobile-open" : ""}`}>
        <div className="logo-container">
          <FaShieldAlt size={28} />
          <h2 className="logo">{translations.sidebar[lang].logo}</h2>
        </div>
        <ul className="menu">
          <li className={`menu-item ${activeTab==='dashboard'?'active':''}`} onClick={() => setActiveTab('dashboard')}>
            <FaChartLine /> <span>{translations.sidebar[lang].menuDashboard}</span>
          </li>
          <li className={`menu-item ${activeTab==='subscription'?'active':''}`} onClick={() => setActiveTab('subscription')}>
            <FaCreditCard /> <span>{translations.sidebar[lang].menuSubscription}</span>
          </li>
          <li className={`menu-item ${activeTab==='notifications'?'active':''}`} onClick={() => setActiveTab('notifications')}>
            <FaBell /> <span>{translations.sidebar[lang].menuNotifications}</span>
            {(unreadCount > 0 || (isAdmin && allPackages.filter(p=>p.status==='pending_review').length > 0)) && <span className="badge">{unreadCount + (isAdmin ? allPackages.filter(p=>p.status==='pending_review').length : 0)}</span>}
          </li>
          <li className={`menu-item ${activeTab==='portfolio'?'active':''}`} onClick={() => setActiveTab('portfolio')}>
            <FaTasks /> <span>{translations.sidebar[lang].menuPortfolio}</span>
          </li>
          {isAdmin && (
            <>
              <li className={`menu-item ${activeTab==='admin_packages'?'active':''}`} onClick={() => setActiveTab('admin_packages')}>
                <FaBoxOpen /> <span>إدارة الباقات</span>
              </li>
              <li className={`menu-item ${activeTab==='admin_blog'?'active':''}`} onClick={() => setActiveTab('admin_blog')}>
                <FaPen /> <span>إدارة المدونة</span>
              </li>
            </>
          )}
        </ul>
        <div className="auto-check-container">
          <div><FaSyncAlt /> <span>{translations.sidebar[lang].autoCheckLabel}</span></div>
          <label className="switch">
            <input type="checkbox" checked={autoCheckEnabled} onChange={() => setAutoCheckEnabled(!autoCheckEnabled)} />
            <span className="slider"></span>
          </label>
        </div>
        <div className="last-checked">
          <FaClock /> {translations.sidebar[lang].lastCheck}: {formatTimeAgo(lastChecked)}
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <div className="navbar">
          <div>
            <h3>{translations.navbar[lang].welcome.replace("{name}", user.full_name)}</h3>
            <p>{translations.navbar[lang].subText}</p>
          </div>
          <div className="nav-actions">
            <button className="icon-btn" onClick={runAllChecks} disabled={loading}>
              <FaSyncAlt className={loading ? "spin" : ""} />
            </button>
            <button className="icon-btn" onClick={() => setShowContactModal(true)}>
              <FaEnvelope />
            </button>
            <div className="avatar">{user.full_name?.charAt(0) || "A"}</div>
          </div>
        </div>

        {/* Summary Cards */}
        {activeTab === 'dashboard' && (
        <div className="cards-grid">
          {isAdmin ? (
            <>
              <div className="card subscription-card" style={{ background: 'linear-gradient(135deg, #0A2E4D, #1E6F8E)' }}>
                  <div className="card-header">
                    <FaShieldAlt size={28} />
                    <h4>لوحة تحكم الإدارة (Admin)</h4>
                  </div>
                  <div>
                    <h1 style={{ fontSize: '3rem', margin: '10px 0' }}>{allUsers.length}</h1>
                    <p>إجمالي المستخدمين المسجلين</p>
                  </div>
              </div>
              <div className="card projects-card">
                  <div className="card-header">
                    <FaTasks size={28} />
                    <h4>إجمالي الاشتراكات</h4>
                  </div>
                  <div>
                    <h1 style={{ fontSize: '3rem', margin: '10px 0', color: '#0E74AB' }}>{allPackages.length}</h1>
                    <p>حزم الباقات النشطة</p>
                  </div>
              </div>
            </>
          ) : (
            <>
              <div className="card subscription-card">
                <div className="card-header">
                  <FaCreditCard size={28} />
                  <h4>{translations.cards[lang].subscription}</h4>
                </div>
                {userPackages.length > 0 ? (
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {userPackages.map((pkg) => (
                      <div key={pkg.id} style={{ paddingBottom: '10px', marginBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                        <p className="plan-name">{pkg.package_name}</p>
                        <div className="status-indicator">
                          <div className={`status-dot ${pkg.status === 'active' ? "active" : "inactive"}`} />
                          <span>{pkg.status === 'active' ? (lang === "ar" ? "نشط" : "Active") : "منتهي"}</span>
                        </div>
                        <p>💰 {pkg.price} EGP</p>
                        <p style={{ fontSize: '0.8rem', opacity: 0.8 }}>📅 {formatDate(pkg.created_at)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>{lang === 'ar' ? 'لا يوجد باقات نشطة' : 'No active subscriptions'}</p>
                )}
              </div>

              <div className="card projects-card">
                <div className="card-header">
                  <FaTasks size={28} />
                  <h4>{translations.cards[lang].projects}</h4>
                </div>
                <div className="project-stats">
                  <div>
                    <span className="stat-number">{projects.filter((p) => p.status === "InProgress").length}</span>
                    <span>{translations.cards[lang].inProgress}</span>
                  </div>
                  <div>
                    <span className="stat-number">{projects.filter((p) => p.status === "PendingReview").length}</span>
                    <span>{translations.cards[lang].pending}</span>
                  </div>
                  <div>
                    <span className="stat-number">{projects.filter((p) => p.status === "Completed").length}</span>
                    <span>{translations.cards[lang].completed}</span>
                  </div>
                </div>
                <button onClick={() => setShowAddProjectModal(true)} className="text-btn">
                  <FaPlus /> {translations.projectTable[lang].addProject}
                </button>
              </div>
            </>
          )}

          {!isAdmin && (
            <div className="card payment-card">
              <div className="card-header">
                <FaWallet size={28} />
                <h4>{translations.cards[lang].paymentHealth}</h4>
              </div>
              <div className="payment-meta">
                <p>يتم إدارة الدفعات عبر الواتساب للعملاء الموثوقين.</p>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Projects / Admin Table */}
        {(activeTab === 'dashboard' || activeTab === 'portfolio') && (
        <div className="table-container">
          <div className="section-header">
            <h4><FaTasks /> {isAdmin ? "جميع اشتراكات المستخدمين" : translations.projectTable[lang].title}</h4>
            <button className="text-btn" onClick={runAllChecks}><MdRefresh /> {translations.projectTable[lang].checkStatus}</button>
          </div>
          <div className="table-responsive">
            {isAdmin ? (
               <table className="data-table">
               <thead>
                 <tr>
                   <th>اسم المستخدم</th>
                   <th>الباقة المشترك بها</th>
                   <th>السعر</th>
                   <th>الحالة</th>
                   <th>تاريخ الاشتراك</th>
                 </tr>
               </thead>
               <tbody>
                 {allPackages.map((pkg) => (
                     <tr key={pkg.id}>
                       <td><strong>{pkg.user_name}</strong></td>
                       <td>{pkg.package_name}</td>
                       <td>{pkg.price} EGP</td>
                       <td>
                          {pkg.status === 'active' ? (
                            <div>
                              <span className="status-badge" style={{ background: '#E8F5E9', color: '#4CAF50', display: 'block', marginBottom: '5px' }}>نشط</span>
                              <button onClick={() => handleDeactivatePackage(pkg.id)} style={{ background: '#e53935', color: 'white', border: 'none', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>إلغاء تنشيط</button>
                            </div>
                          ) : (
                            <div>
                                <span className="status-badge" style={{ background: '#FFF3E0', color: '#FF9800', display: 'block', marginBottom: '5px' }}>مراجعة</span>
                                <button onClick={() => handleActivatePackage(pkg.id)} style={{ background: '#0E74AB', color: 'white', border: 'none', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>تفعيل</button>
                            </div>
                          )}
                       </td>
                       <td>{formatDate(pkg.created_at)}</td>
                     </tr>
                   ))}
               </tbody>
             </table>
            ) : (
                <table className="data-table">
                <thead>
                  <tr>
                    <th>{translations.projectTable[lang].projectName}</th>
                    <th>{translations.projectTable[lang].status}</th>
                    <th>{translations.projectTable[lang].progress}</th>
                    <th>{translations.projectTable[lang].dueDate}</th>
                    <th>{translations.projectTable[lang].lastUpdate}</th>
                    <th>{translations.projectTable[lang].actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => {
                    const status = getProjectStatusInfo(p.status);
                    return (
                      <tr key={p.id}>
                        <td data-label={translations.projectTable[lang].projectName}>{p.name}</td>
                        <td data-label={translations.projectTable[lang].status}>
                          <span className="status-badge" style={{ background: status.bg, color: status.color }}>
                            {status.icon} {status.label}
                          </span>
                        </td>
                        <td data-label={translations.projectTable[lang].progress}>
                          <div className="progress-wrapper">
                            <div className="progress-bar" style={{ width: `${p.progress || 0}%`, background: status.color }} />
                            <span className="progress-text">{p.progress || 0}%</span>
                          </div>
                        </td>
                        <td data-label={translations.projectTable[lang].dueDate}>{formatDate(p.dueDate)}</td>
                        <td data-label={translations.projectTable[lang].lastUpdate}>{formatTimeAgo(p.lastUpdate)}</td>
                        <td data-label={translations.projectTable[lang].actions}>
                          <button className="action-btn edit" onClick={() => setEditingProject(p)}><FaEdit /> {translations.projectTable[lang].edit}</button>
                          <button className="action-btn delete" onClick={() => handleDeleteProject(p.id)}><FaTrash /> {translations.projectTable[lang].delete}</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
        )}
        {(activeTab === 'dashboard' && isAdmin) && (
        <div className="table-container" style={{marginTop:'2rem'}}>
           <div className="section-header">
             <h4><FaShieldAlt /> إدارة المستخدمين المسجلين</h4>
           </div>
           <div className="table-responsive">
             <table className="data-table">
               <thead><tr><th>ID</th><th>الاسم</th><th>البريد</th><th>إجراء</th></tr></thead>
               <tbody>
                  {allUsers.map(u => (
                     <tr key={u.id} style={{ background: u.reset_request == 1 ? '#FFF3E0' : 'inherit' }}>
                        <td>{u.id} {u.role === 'admin' ? '⭐' : ''}</td>
                        <td>
                          {u.full_name} <br/>
                          <small style={{color:'#666'}}>{u.phone || 'لا يوجد هاتف'}</small>
                        </td>
                        <td>{u.email}</td>
                        <td>
                           <button className="action-btn" style={{padding:'5px', background:'#4CAF50', color:'#fff', marginRight:'5px'}} onClick={() => handleOpenPasswordModal(u)}>كلمة المرور {u.reset_request == 1 && '⚠️'}</button>
                           {u.role !== 'admin' && <button className="action-btn" style={{padding:'5px', background:'#0E74AB', color:'#fff', marginRight:'5px'}} onClick={() => handlePromoteUser(u.id)}>ترقية لمشرف</button>}
                           <button className="action-btn delete" style={{padding:'5px 10px'}} onClick={() => handleDeleteUser(u.id)}><FaTrash /></button>
                        </td>
                     </tr>
                  ))}
               </tbody>
             </table>
           </div>
        </div>
        )}

        {/* Payment History */}
        {activeTab === 'subscription' && (
        <>
        <div className="table-container">
          <div className="section-header">
            <h4><FaFileInvoice /> {translations.paymentTable[lang].title}</h4>
            <button className="text-btn" onClick={handleRefreshPaymentHistory}><MdRefresh /> {translations.paymentTable[lang].refresh}</button>
          </div>
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{translations.paymentTable[lang].invoice}</th>
                  <th>{translations.paymentTable[lang].date}</th>
                  <th>{translations.paymentTable[lang].amount}</th>
                  <th>{translations.paymentTable[lang].method}</th>
                  <th>{translations.paymentTable[lang].status}</th>
                  <th>{translations.paymentTable[lang].action}</th>
                </tr>
              </thead>
              <tbody>
                {payments.history?.map((pay) => (
                  <tr key={pay.id}>
                    <td data-label={translations.paymentTable[lang].invoice}>{pay.invoice}</td>
                    <td data-label={translations.paymentTable[lang].date}>{formatDate(pay.date)}</td>
                    <td data-label={translations.paymentTable[lang].amount}>{pay.amount}</td>
                    <td data-label={translations.paymentTable[lang].method}>{pay.method}</td>
                    <td data-label={translations.paymentTable[lang].status}>{pay.status}</td>
                    <td data-label={translations.paymentTable[lang].action}>
                      {pay.status === "paid" ? (
                        <button className="download-btn" onClick={() => alert("تحميل PDF (تجريبي)")}><FaDownload /> {translations.paymentTable[lang].download}</button>
                      ) : pay.status === "failed" ? (
                        <button className="retry-btn" onClick={handlePayMonthly}><MdRefresh /> {translations.paymentTable[lang].retry}</button>
                      ) : (
                        <span>{translations.paymentTable[lang].processing}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="actions-bar">
          <button className="btn-primary" onClick={handlePayMonthly} disabled={paymentProcessing}>
            {paymentProcessing ? <FaSpinner className="spin" /> : <MdPayment />}
            {paymentProcessing ? "Processing..." : translations.buttons[lang].payNow}
          </button>
          <button className="btn-secondary" onClick={() => setShowUpgradeModal(true)}>
            <FaCrown /> {translations.buttons[lang].upgradePlan}
          </button>
          <button className="btn-outline" onClick={() => setShowContactModal(true)}>
            <FaEnvelope /> {translations.buttons[lang].contactSupport}
          </button>
        </div>
        </>
        )}

        {/* Notifications */}
        {activeTab === 'notifications' && (
        <div className="notifications-panel">
          <div className="section-header">
            <h4><FaBell /> {translations.notifications[lang].title}</h4>
            <button className="text-btn" onClick={() => setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))}>
              {translations.buttons[lang].markAllRead}
            </button>
          </div>
          <div className="notifications-list">
             {isAdmin && allPackages.filter(p => p.status === 'pending_review').map(pkg => (
                <div key={'pkg_'+pkg.id} className="notification-item priority-critical">
                   <div className="notif-icon"><FaBell /></div>
                   <div className="notif-content">
                     <p>طلب تفعيل باقة: <strong>{pkg.package_name}</strong> للعميل {pkg.user_name}</p>
                     <button onClick={() => handleActivatePackage(pkg.id)} style={{marginTop:'8px', background:'#4CAF50', color:'white', border:'none', padding:'5px 10px', borderRadius:'4px', cursor:'pointer', fontSize:'12px'}}>تنشيط الباقة</button>
                   </div>
                </div>
             ))}
             {isAdmin && adminLogs.length > 0 && adminLogs.map((log) => (
                <div key={'log_'+log.id} className="notification-item priority-low">
                   <div className="notif-icon"><FaEnvelope /></div>
                   <div className="notif-content">
                     <strong>الزائر ({log.session_id}):</strong>
                     <p style={{marginBottom: '5px', color:'#ccc'}}>{log.user_message}</p>
                     <strong>رد البوت:</strong>
                     <p style={{color:'#64b5f6'}}>{log.bot_response}</p>
                     <span style={{display: 'block', marginTop:'5px'}}>{formatDate(log.created_at)}</span>
                   </div>
                </div>
             ))}
            {notifications.length === 0 && (!isAdmin || (adminLogs.length === 0 && allPackages.filter(p => p.status === 'pending_review').length === 0)) ? (
              <div className="empty-notifications">
                <FaBell size={40} style={{ opacity: 0.3 }} />
                <p>{translations.notifications[lang].noNotifications}</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`notification-item ${n.read ? "read" : ""} priority-${n.priority}`}
                  onClick={() => setNotifications((prev) => prev.map((not) => (not.id === n.id ? { ...not, read: true } : not)))}
                >
                  <div className="notif-icon">{n.type === "project" ? <FaTasks /> : n.type === "payment" ? <MdPayment /> : <FaBell />}</div>
                  <div className="notif-content">
                    <p>{n.message}</p>
                    <span>{formatTimeAgo(n.time)}</span>
                  </div>
                  {!n.read && <div className="unread-dot" />}
                </div>
              ))
            )}
          </div>
        </div>
        )}
      </main>

      {/* Modals */}
      {/* Change Password Modal */}
      {showPasswordModal && selectedUserForPassword && (
        <Modal onClose={() => setShowPasswordModal(false)} title={`تغيير كلمة مرور: ${selectedUserForPassword.full_name}`}>
          <form onSubmit={handleChangePassword}>
            <div className="form-group" style={{marginBottom: '15px'}}>
              <label>كلمة المرور الجديدة</label>
              <input type="text" value={newPasswordValue} onChange={(e) => setNewPasswordValue(e.target.value)} required style={{width:'100%', padding:'10px', marginTop:'5px'}} />
              {selectedUserForPassword.reset_request == 1 && <span style={{color:'red', fontSize:'12px', display:'block', marginTop:'5px'}}>⚠️ هذا العميل يطلب إعادة تعيين باسورد</span>}
            </div>
            <button type="submit" className="btn-primary" style={{width:'100%'}}>حفظ التغييرات</button>
          </form>
        </Modal>
      )}

      {/* Upgrade Plan Modal */}
      {showUpgradeModal && (
        <Modal onClose={() => setShowUpgradeModal(false)} title={translations.upgradeModal[lang].title}>
          {activePackages.map((pkg) => (
            <div key={pkg.id} className="modal-package">
              <h4>{pkg.name}</h4>
              <p>{translations.upgradeModal[lang].price}: {pkg.price}</p>
              <p>{translations.upgradeModal[lang].maxProjects}: {pkg.maxProjects}</p>
              <button onClick={() => handleUpgradePlan(pkg.id)} disabled={upgradingPlan} className="btn-primary">
                {upgradingPlan ? <FaSpinner className="spin" /> : translations.upgradeModal[lang].subscribe}
              </button>
            </div>
          ))}
        </Modal>
      )}

      {showContactModal && (
        <Modal onClose={() => setShowContactModal(false)} title={translations.contactModal[lang].title}>
          <input type="text" placeholder={translations.contactModal[lang].fullName} value={contactForm.fullName} onChange={(e) => setContactForm({ ...contactForm, fullName: e.target.value })} className="modal-input" />
          <input type="email" placeholder={translations.contactModal[lang].email} value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="modal-input" />
          <input type="tel" placeholder={translations.contactModal[lang].phone} value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} className="modal-input" />
          <input type="text" placeholder={translations.contactModal[lang].subject} value={contactForm.subject} onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })} className="modal-input" />
          <textarea placeholder={translations.contactModal[lang].message} value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} className="modal-textarea" />
          <button onClick={handleContactSend} className="btn-primary">{translations.buttons[lang].send}</button>
        </Modal>
      )}

      {showAddProjectModal && (
        <Modal onClose={() => setShowAddProjectModal(false)} title={translations.projectTable[lang].addProject}>
          <input type="text" placeholder="Project Name" value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} className="modal-input" />
          <textarea placeholder="Description" value={newProjectDesc} onChange={(e) => setNewProjectDesc(e.target.value)} className="modal-textarea" />
          <button onClick={handleAddProject} className="btn-primary">{translations.buttons[lang].add}</button>
        </Modal>
      )}

      {editingProject && (
        <Modal onClose={() => setEditingProject(null)} title={translations.projectTable[lang].edit}>
          <input type="text" value={editingProject.name} onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })} className="modal-input" />
          <textarea value={editingProject.description} onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })} className="modal-textarea" />
          <select value={editingProject.status} onChange={(e) => setEditingProject({ ...editingProject, status: e.target.value })} className="modal-input">
            <option value="InProgress">In Progress</option>
            <option value="PendingReview">Pending Review</option>
            <option value="Completed">Completed</option>
          </select>
          <button onClick={handleUpdateProject} className="btn-primary">{translations.buttons[lang].update}</button>
        </Modal>
      )}

      {isAdmin && activeTab === 'admin_packages' && (
        <AdminPackages lang={lang} admin_id={user.id} />
      )}

      {isAdmin && activeTab === 'admin_blog' && (
        <AdminBlog lang={lang} admin_id={user.id} />
      )}

      {showTermsModal && (
        <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.85)' }}>
          <div className="modal-container" style={{ maxWidth: '600px' }}>
            <h3 style={{ color: '#0E74AB', borderBottom: '2px solid #eee', paddingBottom: '10px' }}>
              شروط وأحكام استخدام CodiCal
            </h3>
            <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '15px 0', lineHeight: '1.8', color: '#444' }}>
              <p>مرحباً بك في CodiCal، بمجرد تسجيلك لدينا يجب أن توافق على الشروط التالية:</p>
              <ul>
                <li>جميع الباقات يتم تفعيلها فور طلبها من المنصة والدفع يتم لاحقاً أو عبر القنوات الرسمية.</li>
                <li>يُمنع استخدام البوتات في أي خدمات غير قانونية أو مخالفة.</li>
                <li>الدعم الفني متاح 24/7 عبر لوحة التحكم فقط.</li>
              </ul>
              <p style={{ marginTop: '20px', fontWeight: 'bold' }}>يرجى الموافقة للاستمرار في استخدام لوحة التحكم.</p>
            </div>
            <button onClick={handleAcceptTerms} className="btn-primary" style={{ width: '100%', marginTop: '20px', fontSize: '18px' }}>
              أوافق على الشروط
            </button>
          </div>
        </div>
      )}

      <style>{`
        /* Base Styles & Reset */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', 'Segoe UI', sans-serif;
          background: #F8FAFE;
        }

        .dashboard-container {
          display: flex;
          min-height: 100vh;
          position: relative;
        }

        .rtl {
          direction: rtl;
        }

        .ltr {
          direction: ltr;
        }

        /* Animations */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }

        /* Mobile Menu Toggle */
        .mobile-menu-toggle {
          position: fixed;
          top: 15px;
          left: 15px;
          z-index: 1100;
          background: #0E74AB;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 10px;
          font-size: 20px;
          cursor: pointer;
          display: none;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }

        /* Language Switcher */
        .lang-switcher {
          position: fixed;
          top: 15px;
          right: 15px;
          z-index: 1100;
          background: #0E74AB;
          color: white;
          border: none;
          border-radius: 40px;
          padding: 8px 16px;
          font-weight: bold;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        }

        /* Sidebar */
        .sidebar {
          width: 280px;
          background: linear-gradient(180deg, #0A2E4D 0%, #0E74AB 100%);
          color: #fff;
          padding: 30px 20px;
          position: sticky;
          top: 0;
          height: 100vh;
          overflow-y: auto;
          transition: transform 0.3s ease;
          z-index: 1000;
        }

        .logo-container {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 50px;
        }

        .logo {
          font-size: 24px;
          font-weight: bold;
        }

        .menu {
          list-style: none;
          margin: 0 0 40px 0;
          padding: 0;
        }

        .menu-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 12px;
          margin-bottom: 8px;
          cursor: pointer;
          color: rgba(255,255,255,0.8);
          position: relative;
          transition: all 0.2s;
        }

        .menu-item.active {
          background: rgba(255,255,255,0.15);
          color: #fff;
          font-weight: 600;
        }

        .menu-item:hover {
          background: rgba(255,255,255,0.1);
        }

        .badge {
          position: absolute;
          right: 16px;
          background: #F44336;
          color: #fff;
          border-radius: 10px;
          padding: 2px 8px;
          font-size: 11px;
          font-weight: bold;
        }

        .auto-check-container {
          margin-top: 20px;
          padding: 16px;
          background: rgba(255,255,255,0.1);
          border-radius: 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .switch {
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
        }

        .switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          border-radius: 24px;
          transition: .4s;
        }

        .slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          border-radius: 50%;
          transition: .4s;
        }

        input:checked + .slider {
          background-color: #4CAF50;
        }

        input:checked + .slider:before {
          transform: translateX(20px);
        }

        .last-checked {
          position: absolute;
          bottom: 20px;
          left: 20px;
          right: 20px;
          font-size: 11px;
          opacity: 0.7;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        /* Main Content */
        .main-content {
          flex: 1;
          padding: 30px 40px;
          overflow-x: hidden;
        }

        .navbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          flex-wrap: wrap;
          gap: 15px;
        }

        .nav-actions {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .icon-btn {
          background: transparent;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #5A6E7C;
          padding: 8px;
          border-radius: 8px;
          transition: background 0.2s;
        }

        .icon-btn:hover {
          background: #eef2f6;
        }

        .avatar {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          background: #0E74AB;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 18px;
        }

        /* Cards Grid */
        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 25px;
          margin-bottom: 30px;
        }

        .card {
          border-radius: 20px;
          padding: 24px;
          color: #fff;
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }

        .subscription-card {
          background: linear-gradient(135deg, #0E74AB 0%, #0A5A85 100%);
        }

        .projects-card {
          background: linear-gradient(135deg, #4A90E2 0%, #357ABD 100%);
        }

        .payment-card {
          background: linear-gradient(135deg, #2ECC71 0%, #27AE60 100%);
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
        }

        .plan-name {
          font-size: 20px;
          font-weight: bold;
          margin: 0 0 12px;
        }

        .status-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
        }

        .status-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }

        .status-dot.active {
          background: #4CAF50;
        }

        .status-dot.inactive {
          background: #F44336;
        }

        .cancel-btn {
          background: #F44336;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          color: #fff;
          margin-top: 10px;
          cursor: pointer;
        }

        .project-stats {
          display: flex;
          justify-content: space-between;
          text-align: center;
          margin-bottom: 16px;
        }

        .stat-number {
          display: block;
          font-size: 28px;
          font-weight: bold;
          margin-bottom: 4px;
        }

        .attention-alert {
          background: rgba(255,255,255,0.2);
          padding: 10px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          margin-bottom: 12px;
        }

        .text-btn {
          background: transparent;
          border: none;
          color: #fff;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          padding: 8px 12px;
          border-radius: 8px;
        }

        .payment-meta {
          display: flex;
          gap: 24px;
          margin-top: 8px;
        }

        .payment-meta .amount {
          font-weight: bold;
          font-size: 18px;
        }

        .payment-meta .date {
          font-size: 12px;
          opacity: 0.9;
        }

        /* Tables */
        .table-container {
          background: #fff;
          border-radius: 20px;
          padding: 24px;
          margin-bottom: 30px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          overflow-x: auto;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 10px;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14px;
        }

        .data-table th,
        .data-table td {
          padding: 12px 15px;
          text-align: left;
          border-bottom: 1px solid #eef2f6;
        }

        .rtl .data-table th,
        .rtl .data-table td {
          text-align: right;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
        }

        .progress-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .progress-bar {
          width: 60px;
          height: 4px;
          border-radius: 2px;
        }

        .progress-text {
          font-size: 12px;
        }

        .action-btn {
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-right: 8px;
        }

        .action-btn.edit {
          background: #0E74AB;
          color: #fff;
        }

        .action-btn.delete {
          background: #F44336;
          color: #fff;
        }

        .download-btn, .retry-btn {
          border: none;
          padding: 6px 12px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 12px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        .download-btn {
          background: #F0F2F5;
        }

        .retry-btn {
          background: #FF9800;
          color: #fff;
        }

        /* Actions Bar */
        .actions-bar {
          display: flex;
          gap: 15px;
          margin-bottom: 30px;
          flex-wrap: wrap;
        }

        .btn-primary {
          background: linear-gradient(135deg, #0E74AB 0%, #0A5A85 100%);
          color: #fff;
          border: none;
          padding: 12px 28px;
          border-radius: 12px;
          cursor: pointer;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
        }

        .btn-secondary {
          background: #fff;
          color: #0E74AB;
          border: 2px solid #0E74AB;
          padding: 12px 28px;
          border-radius: 12px;
          cursor: pointer;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
        }

        .btn-outline {
          background: #F8FAFE;
          color: #2C3E50;
          border: 1px solid #E2E8F0;
          padding: 12px 28px;
          border-radius: 12px;
          cursor: pointer;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
        }

        /* Notifications */
        .notifications-panel {
          background: #fff;
          border-radius: 20px;
          padding: 24px;
          margin-bottom: 30px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }

        .notifications-list {
          margin-top: 16px;
          max-height: 400px;
          overflow-y: auto;
        }

        .notification-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          border-radius: 16px;
          margin-bottom: 10px;
          position: relative;
          cursor: pointer;
          background: #fff;
          border-left: 4px solid #0E74AB;
          transition: all 0.2s;
        }

        .notification-item.read {
          opacity: 0.7;
        }

        .notification-item.priority-critical {
          border-left-color: #F44336;
        }

        .notification-item.priority-high {
          border-left-color: #FF9800;
        }

        .notif-icon {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: #F0F7FF;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }

        .notif-content {
          flex: 1;
        }

        .unread-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #0E74AB;
          position: absolute;
          right: 20px;
          top: 20px;
        }

        .empty-notifications {
          text-align: center;
          padding: 40px;
          color: #9E9E9E;
        }

        /* Modals */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 2000;
        }

        .modal-container {
          background: white;
          padding: 30px;
          border-radius: 20px;
          min-width: 300px;
          max-width: 500px;
          width: 90%;
          position: relative;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-close {
          position: absolute;
          top: 10px;
          right: 10px;
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
        }

        .modal-input, .modal-textarea {
          width: 100%;
          padding: 12px;
          margin: 10px 0;
          border-radius: 8px;
          border: 1px solid #ccc;
          font-size: 14px;
        }

        .modal-textarea {
          min-height: 80px;
        }

        .modal-package {
          margin-bottom: 16px;
          border-bottom: 1px solid #eee;
          padding-bottom: 12px;
        }

        /* Responsive Styles */
        @media (max-width: 992px) {
          .main-content {
            padding: 20px;
          }
          
          .cards-grid {
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .mobile-menu-toggle {
            display: block;
          }

          .sidebar {
            position: fixed;
            top: 0;
            left: 0;
            transform: translateX(-100%);
            width: 280px;
            z-index: 1050;
            height: 100%;
            transition: transform 0.3s ease;
          }

          .sidebar.mobile-open {
            transform: translateX(0);
          }

          .main-content {
            padding: 70px 15px 20px 15px;
            width: 100%;
          }

          .navbar {
            flex-direction: column;
            align-items: flex-start;
          }

          .nav-actions {
            align-self: flex-end;
          }

          .cards-grid {
            grid-template-columns: 1fr;
          }

          .data-table thead {
            display: none;
          }

          .data-table tr {
            display: block;
            margin-bottom: 15px;
            border: 1px solid #eef2f6;
            border-radius: 12px;
            padding: 10px;
          }

          .data-table td {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px;
            border-bottom: 1px solid #eef2f6;
            text-align: right;
          }

          .data-table td:before {
            content: attr(data-label);
            font-weight: bold;
            margin-left: 10px;
          }

          .rtl .data-table td:before {
            margin-left: 0;
            margin-right: 10px;
          }

          .actions-bar {
            flex-direction: column;
          }

          .btn-primary, .btn-secondary, .btn-outline {
            width: 100%;
            justify-content: center;
          }

          .notification-item {
            flex-wrap: wrap;
          }
        }

        @media (max-width: 480px) {
          .lang-switcher {
            top: 60px;
            right: 15px;
          }

          .card {
            padding: 18px;
          }

          .plan-name {
            font-size: 18px;
          }

          .stat-number {
            font-size: 24px;
          }

          .action-btn {
            padding: 4px 8px;
            font-size: 10px;
          }

          .modal-container {
            padding: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default SmartDashboard;